import React, { useState, useEffect, useCallback } from "react"
import { X, Calendar, Clock, Link, MoreHorizontal, Plus, Zap } from "lucide-react"
import "./TriggersPanel.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"

// In the TriggersPanel component parameters, add initialEnabledTriggers prop
const TriggersPanel = ({ onClose, initialTriggerId = null, onEnabledTriggersChange, initialEnabledTriggers = [], workflowId = null, nodeId = null, csrf_token }) => {
  const { syncNodeTriggers, getNodeTriggers } = useWorkflow();
  
  // Function to create initial triggers with pre-populated data from initialEnabledTriggers
  const createInitialTriggers = useCallback(() => {
    // Default triggers structure
    const defaultTriggers = [
      {
        id: "schedule-1",
        type: "schedule",
        name: "Schedule",
        description: "Every 1 minute",
        enabled: false,
        config: {
          triggerType: "schedule",
          scheduleType: "interval",
          timezone: "UTC",
          frequency: "minute",
          minuteValue: "0",
          hourValue: "0",
          dayValue: "Monday",
          monthValue: "1st",
          timeValue: "12:00 AM",
          cronExpression: "* * * * *",
          minuteInterval: 1,
        },
      },
      {
        id: "webhook-acculynx",
        type: "acculynx",
        name: "Acculynx",
        description: "Acculynx webhook integration",
        enabled: false,
        config: {
          url: "https://api.acculynx.com/webhook",
          method: "POST",
          headers: [],
        },
      },
      {
        id: "webhook-leadperfection",
        type: "leadperfection",
        name: "Leadperfection",
        description: "Leadperfection webhook integration",
        enabled: false,
        config: {
          url: "https://api.leadperfection.com/webhook",
          method: "POST",
          headers: [],
        },
      },
      {
        id: "integration-aspire",
        type: "aspire",
        name: "Aspire",
        description: "Aspire integration",
        enabled: false,
        config: {
          url: "https://api.aspire.com/integration",
          method: "POST",
          headers: [],
        },
      },
    ];

    // If we have initial data, merge it with defaults
    if (initialEnabledTriggers && initialEnabledTriggers.length > 0) {
      return defaultTriggers.map(defaultTrigger => {
        // Find matching trigger in initialEnabledTriggers by type
        const initialTrigger = initialEnabledTriggers.find(trigger => trigger.type === defaultTrigger.type);
        
        if (initialTrigger) {
          return {
            ...defaultTrigger,
            enabled: initialTrigger.enabled,
            name: initialTrigger.name || defaultTrigger.name,
            description: initialTrigger.description || defaultTrigger.description,
            config: initialTrigger.config || defaultTrigger.config
          };
        }
        
        return defaultTrigger;
      });
    }
    
    return defaultTriggers;
  }, [initialEnabledTriggers]);
  
  // State for managing triggers - initialize with empty array, will be set in useEffect
  const [triggers, setTriggers] = useState([])

  // State for managing the active trigger being edited
  const [activeTrigger, setActiveTrigger] = useState(null)
  const [isCreatingTrigger, setIsCreatingTrigger] = useState(false)
  const [showDeleteMenu, setShowDeleteMenu] = useState(null)

  // State for local config, used when editing or creating a trigger
  const [localConfig, setLocalConfig] = useState({
    triggerType: "schedule",
    scheduleType: "interval",
    timezone: "UTC",
    frequency: "day",
    minuteValue: "0",
    hourValue: "0",
    dayValue: ["Monday"],
    monthValue: "1st",
    quarterValue: "Q1",
    halfYearValue: "H1",
    annualMonthValue: "January",
    timeValue: "12:00 AM",
    url: "https://example.com/webhook",
    name: "",
    startDate: new Date().toISOString().split("T")[0],
    startTime: new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    }),
    // Active timeframe settings
    enableTimeframe: false,
    timeframeStart: "06:00",
    timeframeEnd: "17:00",
    timeframeDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    timeframeOvernight: false,
    endType: "never",
    endDate: "",
    endOccurrences: 1,
    // Add minute interval for minute frequency
    minuteInterval: 1,
  })

  // Set the active trigger based on the initialTriggerId prop
  useEffect(() => {
    if (initialTriggerId) {
      const trigger = triggers.find((t) => t.id === initialTriggerId)
      if (trigger) {
        // Ensure dayValue is always an array and minuteInterval is set
        const config = {
          ...trigger.config,
          dayValue: Array.isArray(trigger.config.dayValue) ? trigger.config.dayValue : [trigger.config.dayValue],
          minuteInterval: trigger.config.minuteInterval || 1
        }
        setActiveTrigger(trigger)
        setLocalConfig(config)
        setIsCreatingTrigger(false)
      }
    }
  }, [initialTriggerId, triggers])

  // Initialize and update triggers when component mounts or initialEnabledTriggers changes
  useEffect(() => {
    setTriggers(createInitialTriggers());
  }, [createInitialTriggers])



  // Function to toggle a trigger's enabled state
  const toggleTrigger = async (triggerId) => {
    console.log(`Toggling trigger: ${triggerId}`)
    
    setTriggers((prevTriggers) => {
      const updatedTriggers = prevTriggers.map((trigger) =>
        trigger.id === triggerId ? { ...trigger, enabled: !trigger.enabled } : trigger,
      )

      console.log('Updated triggers after toggle:', updatedTriggers)
      
      // Notify parent about the change
      if (onEnabledTriggersChange) {
        const enabled = updatedTriggers.filter((t) => t.enabled)
        console.log('Calling onEnabledTriggersChange with enabled triggers:', enabled)
        onEnabledTriggersChange(enabled)
      }

      // Sync with database if workflowId and nodeId are provided
      if (workflowId && nodeId) {
        console.log(`Syncing to database for workflow ${workflowId}, node ${nodeId}`)
        syncTriggersToDatabase(updatedTriggers)
      } else {
        console.warn('Cannot sync to database - missing workflowId or nodeId:', { workflowId, nodeId })
      }

      return updatedTriggers
    })
  }

  // Function to sync triggers to database
  const syncTriggersToDatabase = async (triggersToSync) => {
    if (!workflowId || !nodeId) return

    try {
      // Send ALL triggers (enabled and disabled) so backend can sync complete state
      const triggerData = triggersToSync.map(trigger => ({
        id: trigger.id,
        type: trigger.type,
        name: trigger.name,
        description: trigger.description,
        enabled: trigger.enabled,
        config: trigger.config
      }))

      await syncNodeTriggers(workflowId, nodeId, triggerData, null, csrf_token)
    } catch (error) {
      console.error('Failed to sync triggers to database:', error)
    }
  }

  // Function to delete a trigger
  const deleteTrigger = (triggerId) => {
    setTriggers((prevTriggers) => {
      const updatedTriggers = prevTriggers.filter((trigger) => trigger.id !== triggerId)

      // Notify parent about the change if a deleted trigger was enabled
      if (onEnabledTriggersChange && prevTriggers.some((t) => t.id === triggerId && t.enabled)) {
        const enabled = updatedTriggers.filter((t) => t.enabled)
        onEnabledTriggersChange(enabled)
      }

      // Sync to database if workflowId and nodeId are provided
      if (workflowId && nodeId) {
        console.log('Syncing trigger deletion to database')
        syncTriggersToDatabase(updatedTriggers)
      }

      return updatedTriggers
    })

    setShowDeleteMenu(null)
    if (activeTrigger?.id === triggerId) {
      setActiveTrigger(null)
    }
  }

  // Function to save trigger changes
  const saveTriggerChanges = (updatedConfig) => {
    setTriggers((prevTriggers) => {
      const updated = prevTriggers.map((trigger) => {
        if (trigger.id === activeTrigger.id) {
          // Update description based on config
          let description = ""
          if (updatedConfig.triggerType === "schedule") {
            if (updatedConfig.frequency === "minute") {
              description = `Every ${updatedConfig.minuteInterval} minute${updatedConfig.minuteInterval !== 1 ? 's' : ''}`
            } else if (updatedConfig.frequency === "hour") {
              description = `At ${updatedConfig.minuteValue} minutes past the hour`
            } else if (updatedConfig.frequency === "day") {
              description = `At ${updatedConfig.timeValue} every day`
            } else if (updatedConfig.frequency === "week") {
              const days = Array.isArray(updatedConfig.dayValue)
                ? updatedConfig.dayValue.join(", ")
                : updatedConfig.dayValue
              description = `At ${updatedConfig.timeValue} (${updatedConfig.timezone}), only on ${days}`
            } else if (updatedConfig.frequency === "month") {
              description = `At ${updatedConfig.timeValue} (${updatedConfig.timezone}), on the ${updatedConfig.monthValue} of every month`
            }
          }

          return {
            ...trigger,
            name: updatedConfig.name || trigger.name,
            config: updatedConfig,
            description: description || trigger.description,
          }
        }
        return trigger
      })

      // Notify parent about the change if there are enabled triggers
      if (onEnabledTriggersChange) {
        const enabled = updated.filter((t) => t.enabled)
        onEnabledTriggersChange(enabled)
      }

      // Sync changes to database if workflowId and nodeId are provided
      if (workflowId && nodeId) {
        syncTriggersToDatabase(updated)
      }

      return updated
    })

    setActiveTrigger(null)
  }

  // Function to create a new trigger
  const createTrigger = (config) => {
    // Generate description based on config
    let description = ""
    if (config.triggerType === "schedule") {
      if (config.frequency === "minute") {
        description = `Every ${config.minuteInterval} minute${config.minuteInterval !== 1 ? 's' : ''}`
      } else if (config.frequency === "hour") {
        description = `At ${config.minuteValue} minutes past the hour`
      } else if (config.frequency === "day") {
        description = `At ${config.timeValue} every day`
      } else if (config.frequency === "week") {
        const days = Array.isArray(config.dayValue) ? config.dayValue.join(", ") : config.dayValue
        description = `At ${config.timeValue} (${config.timezone}), only on ${days}`
      } else if (config.frequency === "month") {
        description = `At ${config.timeValue} (${config.timezone}), on the ${config.monthValue} of every month`
      }
    } else if (config.triggerType === "webhook") {
      description = config.url
    }

    const newTrigger = {
      id: `${config.triggerType}-${Date.now()}`,
      type: config.triggerType,
      name: config.name || (config.triggerType === "schedule" ? "Schedule" : "Webhook"),
      description,
      enabled: false,
      config,
    }

    setTriggers((prevTriggers) => {
      const updatedTriggers = [...prevTriggers, newTrigger]
      
      // Sync to database if workflowId and nodeId are provided
      if (workflowId && nodeId) {
        console.log('Syncing new trigger to database')
        syncTriggersToDatabase(updatedTriggers)
      }
      
      return updatedTriggers
    })

    setIsCreatingTrigger(false)
  }

  // Generate options for minute dropdown
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i)

  // Generate options for hour dropdown
  const hourOptions = Array.from({ length: 60 }, (_, i) => `at ${i} minutes past the hour`)

  // Generate options for day dropdown
  const dayOptions = [
    { value: "Monday", label: "Mon" },
    { value: "Tuesday", label: "Tue" },
    { value: "Wednesday", label: "Wed" },
    { value: "Thursday", label: "Thu" },
    { value: "Friday", label: "Fri" },
    { value: "Saturday", label: "Sat" },
    { value: "Sunday", label: "Sun" },
  ]

  // Function to toggle a day in the dayValue array
  const toggleDay = (day) => {
    const currentDays = Array.isArray(localConfig.dayValue) ? [...localConfig.dayValue] : [localConfig.dayValue]

    if (currentDays.includes(day)) {
      // Remove the day if it's already selected
      const newDays = currentDays.filter((d) => d !== day)
      // Ensure at least one day is selected
      if (newDays.length === 0) return
      updateConfig("dayValue", newDays)
    } else {
      // Add the day if it's not already selected
      updateConfig("dayValue", [...currentDays, day])
    }
  }

  // Function to toggle a day in the timeframeDays array
  const toggleTimeframeDay = (day) => {
    const currentDays = Array.isArray(localConfig.timeframeDays) ? [...localConfig.timeframeDays] : []

    if (currentDays.includes(day)) {
      // Remove the day if it's already selected
      const newDays = currentDays.filter((d) => d !== day)
      // Ensure at least one day is selected
      if (newDays.length === 0) return
      updateConfig("timeframeDays", newDays)
    } else {
      // Add the day if it's not already selected
      updateConfig("timeframeDays", [...currentDays, day])
    }
  }

  // Generate options for month dropdown
  const monthOptions = [
    "1st",
    "2nd",
    "3rd",
    "4th",
    "5th",
    "6th",
    "7th",
    "8th",
    "9th",
    "10th",
    "11th",
    "12th",
    "13th",
    "14th",
    "15th",
    "16th",
    "17th",
    "18th",
    "19th",
    "20th",
    "21st",
    "22nd",
    "23rd",
    "24th",
    "25th",
    "26th",
    "27th",
    "28th",
    "29th",
    "30th",
    "31st",
  ]

  // Generate options for time dropdown
  const timeOptions = [
    "12:00 AM",
    "1:00 AM",
    "2:00 AM",
    "3:00 AM",
    "4:00 AM",
    "5:00 AM",
    "6:00 AM",
    "7:00 AM",
    "8:00 AM",
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
    "7:00 PM",
    "8:00 PM",
    "9:00 PM",
    "10:00 PM",
    "11:00 PM",
  ]

  const updateConfig = (key, newValue) => {
    const newConfig = { ...localConfig, [key]: newValue };
    
    // Auto-uncheck overnight checkbox when timeframe no longer spans overnight
    if ((key === 'timeframeStart' || key === 'timeframeEnd') && localConfig.timeframeOvernight) {
      try {
        const startTime = key === 'timeframeStart' ? newValue : localConfig.timeframeStart;
        const endTime = key === 'timeframeEnd' ? newValue : localConfig.timeframeEnd;
        
        if (startTime && endTime) {
          const start = new Date(`2000-01-01T${startTime}`);
          const end = new Date(`2000-01-01T${endTime}`);
          
          // If end time is now after start time (same day), auto-uncheck overnight
          if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start) {
            newConfig.timeframeOvernight = false;
          }
        }
      } catch (error) {
        // If there's an error parsing times, leave the checkbox as is
      }
    }
    
    setLocalConfig(newConfig);
  }

  // Function to validate active timeframe settings
  const validateTimeframe = () => {
    if (!localConfig.enableTimeframe || !['minute', 'hour'].includes(localConfig.frequency)) {
      return { isValid: true, error: null };
    }

    // Check if time values are set
    if (!localConfig.timeframeStart || !localConfig.timeframeEnd) {
      return { isValid: false, error: 'Start and end times must be set for active timeframe' };
    }

    // Check if time values are valid format
    try {
      const start = new Date(`2000-01-01T${localConfig.timeframeStart}`);
      const end = new Date(`2000-01-01T${localConfig.timeframeEnd}`);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return { isValid: false, error: 'Invalid time format' };
      }
      
      // Check if start and end times are identical
      if (localConfig.timeframeStart === localConfig.timeframeEnd) {
        return { isValid: false, error: 'Start and end times cannot be identical' };
      }
      
      // Check if end time is before start time and overnight checkbox is not checked
      if (end <= start && !localConfig.timeframeOvernight) {
        return { isValid: false, error: 'Check "Runs overnight" for timeframes that span across midnight' };
      }
      
      // Check if overnight checkbox is checked but times don't actually span overnight
      if (localConfig.timeframeOvernight && end > start) {
        return { isValid: false, error: 'Uncheck "Runs overnight" - your timeframe is within the same day' };
      }
    } catch (error) {
      return { isValid: false, error: 'Invalid time format' };
    }

    // Check if at least one day is selected
    const selectedDays = Array.isArray(localConfig.timeframeDays) ? localConfig.timeframeDays : [];
    if (selectedDays.length === 0) {
      return { isValid: false, error: 'At least one day must be selected for active timeframe' };
    }

    return { isValid: true, error: null };
  }

  // Function to render the trigger configuration panel
  const renderTriggerConfig = (isNew = false) => {
    // Determine which config to use (either activeTrigger's or localConfig for new triggers)
    const config = isNew ? localConfig : activeTrigger?.config

    // If config is undefined, return null to avoid errors
    if (!config) return null

    const handleSave = () => {
      const validation = validateTimeframe();
      if (!validation.isValid) {
        alert(validation.error);
        return;
      }

      if (isNew) {
        createTrigger(localConfig)
      } else {
        saveTriggerChanges(localConfig)
      }
    }

    return (
      <div className="trigger-config bg-white dark:bg-card border-l dark:border-l-[#1f2937]">
        <div className="trigger-config-header bg-white dark:border-b-2 dark:border-[#1f2937] dark:bg-card">
          <div className="trigger-config-title">
            {isNew ? (
              <>
                <Zap size={16} />
                <span>New trigger</span>
              </>
            ) : (
              <>
                <Calendar size={16} />
                <span>{config.triggerType === "schedule" ? "Schedule" : "Webhook"}</span>
              </>
            )}
          </div>
          <button
            className="close-button dark:text-white dark:hover:bg-red-500"
            onClick={() => {
              if (isNew) {
                setIsCreatingTrigger(false)
              } else {
                setActiveTrigger(null)
              }
              // Reset local config when closing the config panel
                              setLocalConfig({
                  triggerType: "schedule",
                  scheduleType: "interval",
                  timezone: "UTC",
                  frequency: "day",
                  minuteValue: "0",
                  hourValue: "0",
                  dayValue: ["Monday"],
                  monthValue: "1st",
                  quarterValue: "Q1",
                  halfYearValue: "H1",
                  annualMonthValue: "January",
                  timeValue: "12:00 AM",
                  url: "https://example.com/webhook",
                  name: "",
                  startDate: new Date().toISOString().split("T")[0],
                  startTime: new Date().toLocaleTimeString("en-US", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                  enableTimeframe: false,
                  timeframeStart: "06:00",
                  timeframeEnd: "17:00",
                  timeframeDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                  timeframeOvernight: false,
                  endType: "never",
                  endDate: "",
                  endOccurrences: 1,
                  minuteInterval: 1,
                })
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="trigger-config-content">
          <div className="form-group">
            <label className="dark:text-white">Trigger Name</label>
            <input
              type="text"
              value={localConfig.name || ""}
              onChange={(e) => updateConfig("name", e.target.value)}
              className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
              placeholder="Enter a name for this trigger"
            />
          </div>

          {/* Only show trigger type selection when creating a new trigger */}
          {isNew && (
            <div className="form-group">
              <label className="dark:text-white">Trigger type</label>
              <div className="select-container">
                <div className="select-icon">
                  {localConfig.triggerType === "schedule" ? <Calendar size={16} /> : <Link size={16} />}
                </div>
                <select
                  value={localConfig.triggerType}
                  onChange={(e) => {
                    updateConfig("triggerType", e.target.value)
                  }}
                  className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                >
                  <option value="schedule">Schedule</option>
                  <option value="webhook">Webhook</option>
                  <option value="integration">Integration</option>
                </select>
              </div>
            </div>
          )}

          {localConfig.triggerType === "schedule" ? (
            <>
              {/* Schedule type is now fixed to interval */}
              <input type="hidden" value="interval" />

              <div className="form-group">
                <label className="dark:text-white">Timezone</label>
                <div className="select-container">
                  <div className="select-icon">
                    <Clock size={16} />
                  </div>
                  <select
                    value={localConfig.timezone}
                    onChange={(e) => updateConfig("timezone", e.target.value)}
                    className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="dark:text-white">Start Date & Time</label>
                <div className="date-time-inputs">
                  <input
                    type="date"
                    value={localConfig.startDate}
                    onChange={(e) => updateConfig("startDate", e.target.value)}
                    className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none date-input"
                  />
                  <input
                    type="time"
                    value={localConfig.startTime}
                    onChange={(e) => updateConfig("startTime", e.target.value)}
                    className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none time-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="dark:text-white">Run every</label>
                <div className={`frequency-and-interval-container ${localConfig.frequency === "minute" ? "with-interval" : ""}`}>
                  <div className="select-container no-icon">
                    <select
                      value={localConfig.frequency}
                      onChange={(e) => updateConfig("frequency", e.target.value)}
                      className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    >
                      <option value="minute">minute</option>
                      <option value="hour">hour</option>
                      <option value="day">day</option>
                      <option value="week">week</option>
                      <option value="month">month</option>
                      <option value="quarter">quarter</option>
                      <option value="biannual">biannual</option>
                      <option value="annual">annual</option>
                    </select>
                  </div>

                  {/* Minute interval input - only show when frequency is minute */}
                  {localConfig.frequency === "minute" && (
                    <div className="minute-interval-container">
                      <button
                        type="button"
                        className="interval-arrow dark:bg-[#1f2937] dark:text-white dark:border-none dark:hover:bg-[#374151] dark:focus:outline-none"
                        onClick={() => {
                          const currentValue = localConfig.minuteInterval;
                          const options = [1, 5, 10, 15, 30, 45];
                          const currentIndex = options.indexOf(currentValue);
                          const newIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
                          updateConfig("minuteInterval", options[newIndex]);
                        }}
                      >
                        ▼
                      </button>
                      <div className="interval-value dark:bg-[#1f2937] dark:text-white border-none focus:outline-none">
                        {localConfig.minuteInterval || 1}
                      </div>
                      <button
                        type="button"
                        className="interval-arrow dark:bg-[#1f2937] dark:text-white dark:border-none dark:hover:bg-[#374151] dark:focus:outline-none"
                        onClick={() => {
                          const currentValue = localConfig.minuteInterval;
                          const options = [1, 5, 10, 15, 30, 45];
                          const currentIndex = options.indexOf(currentValue);
                          const newIndex = currentIndex >= options.length - 1 ? 0 : currentIndex + 1;
                          updateConfig("minuteInterval", options[newIndex]);
                        }}
                      >
                        ▲
                      </button>
                      <span className="interval-unit dark:text-white">minute{localConfig.minuteInterval !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>

              {localConfig.frequency === "hour" && (
                <div className="form-group">
                  <div className="select-container no-icon">
                    <select
                      value={`at ${localConfig.minuteValue} minutes past the hour`}
                      onChange={(e) => {
                        const match = e.target.value.match(/at (\d+) minutes/)
                        if (match) updateConfig("minuteValue", match[1])
                      }}
                      className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    >
                      {hourOptions.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {localConfig.frequency === "day" && (
                <div className="form-group">
                  <div className="select-container no-icon">
                    <select
                      value={localConfig.timeValue}
                      onChange={(e) => updateConfig("timeValue", e.target.value)}
                      className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    >
                      {timeOptions.map((option, index) => (
                        <option key={index} value={option}>
                          at {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {localConfig.frequency === "week" && (
                <>
                  <div className="form-group">
                    <div className="form-label-row">
                      <label className="dark:text-white">On:</label>
                    </div>
                    <div className="day-selection">
                      {dayOptions.map((day) => {
                        const isSelected = Array.isArray(localConfig.dayValue)
                          ? localConfig.dayValue.includes(day.value)
                          : localConfig.dayValue === day.value
                        return (
                          <button
                            key={day.value}
                            type="button"
                            className={`day-button dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none ${isSelected ? "selected dark:bg-[#3B82F6] dark:text-white dark:border-none dark:focus:outline-none" : ""}`}
                            onClick={() => toggleDay(day.value)}
                          >
                            {day.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="form-label-row">
                      <label className="dark:text-white">At:</label>
                    </div>
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.timeValue}
                        onChange={(e) => updateConfig("timeValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {timeOptions.map((option, index) => (
                          <option key={index} value={option}>
                            at {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {localConfig.frequency === "month" && (
                <>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.monthValue}
                        onChange={(e) => updateConfig("monthValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {monthOptions.map((option, index) => (
                          <option key={index} value={option}>
                            on the {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.timeValue}
                        onChange={(e) => updateConfig("timeValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {timeOptions.map((option, index) => (
                          <option key={index} value={option}>
                            at {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {localConfig.frequency === "quarter" && (
                <>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.quarterValue || "Q1"}
                        onChange={(e) => updateConfig("quarterValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        <option value="Q1">Starting January (Jan, Apr, Jul, Oct)</option>
                        <option value="Q2">Starting February (Feb, May, Aug, Nov)</option>
                        <option value="Q3">Starting March (Mar, Jun, Sep, Dec)</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.monthValue || "1st"}
                        onChange={(e) => updateConfig("monthValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {monthOptions.map((option, index) => (
                          <option key={index} value={option}>
                            on the {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.timeValue}
                        onChange={(e) => updateConfig("timeValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {timeOptions.map((option, index) => (
                          <option key={index} value={option}>
                            at {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {localConfig.frequency === "biannual" && (
                <>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.halfYearValue || "H1"}
                        onChange={(e) => updateConfig("halfYearValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        <option value="H1">January & July</option>
                        <option value="H2">February & August</option>
                        <option value="H3">March & September</option>
                        <option value="H4">April & October</option>
                        <option value="H5">May & November</option>
                        <option value="H6">June & December</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.monthValue || "1st"}
                        onChange={(e) => updateConfig("monthValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {monthOptions.map((option, index) => (
                          <option key={index} value={option}>
                            on the {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.timeValue}
                        onChange={(e) => updateConfig("timeValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {timeOptions.map((option, index) => (
                          <option key={index} value={option}>
                            at {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {localConfig.frequency === "annual" && (
                <>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.annualMonthValue || "January"}
                        onChange={(e) => updateConfig("annualMonthValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.monthValue || "1st"}
                        onChange={(e) => updateConfig("monthValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {monthOptions.map((option, index) => (
                          <option key={index} value={option}>
                            on the {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.timeValue}
                        onChange={(e) => updateConfig("timeValue", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        {timeOptions.map((option, index) => (
                          <option key={index} value={option}>
                            at {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Environment option removed */}
            </>
          ) : (
            // Webhook configuration
            <>
              <div className="form-group">
                <label className="dark:text-white">Webhook URL</label>
                <input
                  type="text"
                  value={localConfig.url || ""}
                  onChange={(e) => updateConfig("url", e.target.value)}
                  className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                  placeholder="https://example.com/webhook"
                />
              </div>

              {/* Acculynx-specific fields */}
              {localConfig.url && localConfig.url.includes("acculynx") && (
                <>
                  <div className="form-group">
                    <label className="dark:text-white">Webhook Type</label>
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.webhookType || ""}
                        onChange={(e) => updateConfig("webhookType", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        <option value="">Select type...</option>
                        <option value="job_milestone_changed">Job Milestone Changed</option>
                        <option value="job_updated">Job Updated</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="dark:text-white">Trade Type</label>
                    <input
                      type="text"
                      value={localConfig.tradeType || ""}
                      onChange={(e) => updateConfig("tradeType", e.target.value)}
                      className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      placeholder="e.g. Roofing"
                    />
                  </div>
                  <div className="form-group">
                    <label className="dark:text-white"  >Company Rep</label>
                    <input
                      type="text"
                      value={localConfig.companyRep || ""}
                      onChange={(e) => updateConfig("companyRep", e.target.value)}
                      className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label className="dark:text-white">Current Milestone</label>
                    <input
                      type="text"
                      value={localConfig.currentMilestone || ""}
                      onChange={(e) => updateConfig("currentMilestone", e.target.value)}
                      className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      placeholder="e.g. Inspection"
                    />
                  </div>
                </>
              )}

              {/* Leadperfection-specific fields */}
              {localConfig.url && localConfig.url.includes("leadperfection") && (
                <>
                  <div className="form-group">
                    <label className="dark:text-white">Webhook Type</label>
                    <div className="select-container no-icon">
                      <select
                        value={localConfig.webhookType || ""}
                        onChange={(e) => updateConfig("webhookType", e.target.value)}
                        className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      >
                        <option value="">Select type...</option>
                        <option value="lead_status_change">Lead Status Change</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="dark:text-white">Old Status</label>
                    <input
                      type="text"
                      value={localConfig.oldStatus || ""}
                      onChange={(e) => updateConfig("oldStatus", e.target.value)}
                      className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      placeholder="e.g. New Lead"
                    />
                  </div>
                  <div className="form-group">
                    <label className="dark:text-white">New Status</label>
                    <input
                      type="text"
                      value={localConfig.newStatus || ""}
                      onChange={(e) => updateConfig("newStatus", e.target.value)}
                      className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                      placeholder="e.g. Contacted"
                    />
                  </div>
                </>
              )}
            </>
          )}

          {localConfig.triggerType === "schedule" && (
            <>
              {/* Active Timeframe Section - only show for minute and hour frequencies */}
              {(localConfig.frequency === "minute" || localConfig.frequency === "hour") && (
                <>
                  {/* Section Header with Toggle */}
                  <div className="form-section mt-5">
                    <div className="form-section-header">
                      <div className="form-section-title">
                        <Clock size={16} className="text-blue-400" />
                        <label className="dark:text-white font-medium">Active Timeframe</label>
                      </div>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={localConfig.enableTimeframe}
                          onChange={(e) => {
                            const isEnabled = e.target.checked;
                            
                            // Set multiple values at once when first enabled
                            if (isEnabled) {
                              setLocalConfig(prev => ({
                                ...prev,
                                enableTimeframe: true,
                                timeframeStart: prev.timeframeStart || "06:00",
                                timeframeEnd: prev.timeframeEnd || "17:00",
                                timeframeDays: prev.timeframeDays && prev.timeframeDays.length > 0 
                                  ? prev.timeframeDays 
                                  : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
                              }));
                            } else {
                              updateConfig("enableTimeframe", isEnabled);
                            }
                          }}
                        />
                        <span className={`slider round ${localConfig.enableTimeframe ? "" : "dark:bg-[#1f2937]"}`}></span>
                      </label>
                    </div>
                    <p className="form-section-description dark:text-gray-400">
                      Restrict when this trigger can run (e.g., work hours: 9 AM - 5 PM)
                    </p>
                  </div>

                  {/* Time Controls - only show when enabled */}
                  {localConfig.enableTimeframe && (
                    <div className="timeframe-controls bg-[#f9fafb] dark:bg-[#1f2937] border border-[#e2e8f0] dark:border-none">
                      <div className="time-inputs-row">
                        <div className="time-input-group">
                          <label className="time-label dark:text-white">From</label>
                          <input
                            type="time"
                            value={localConfig.timeframeStart}
                            onChange={(e) => updateConfig("timeframeStart", e.target.value)}
                            className={`form-input dark:bg-[#0f172a] dark:text-white dark:border-none dark:focus:outline-none time-input ${(() => {
                              const validation = validateTimeframe();
                              return !validation.isValid && (validation.error?.includes('Check "Runs overnight"') || validation.error?.includes('Uncheck "Runs overnight"') || validation.error?.includes('cannot be identical')) ? 'error' : '';
                            })()}`}
                          />
                        </div>
                        
                        <div className={`time-separator dark:text-gray-400 ${(() => {
                          const validation = validateTimeframe();
                          return !validation.isValid && (validation.error?.includes('Check "Runs overnight"') || validation.error?.includes('Uncheck "Runs overnight"') || validation.error?.includes('cannot be identical')) ? 'error' : '';
                        })()}`}>
                          {(() => {
                            const validation = validateTimeframe();
                            return !validation.isValid && (validation.error?.includes('Check "Runs overnight"') || validation.error?.includes('Uncheck "Runs overnight"') || validation.error?.includes('cannot be identical')) ? '❌' : 'to';
                          })()}
                        </div>
                        
                        <div className="time-input-group">
                          <label className="time-label dark:text-white">To</label>
                          <input
                            type="time"
                            value={localConfig.timeframeEnd}
                            onChange={(e) => updateConfig("timeframeEnd", e.target.value)}
                            className={`form-input dark:bg-[#0f172a] dark:text-white dark:border-none dark:focus:outline-none time-input ${(() => {
                              const validation = validateTimeframe();
                              return !validation.isValid && (validation.error?.includes('Check "Runs overnight"') || validation.error?.includes('Uncheck "Runs overnight"') || validation.error?.includes('cannot be identical')) ? 'error' : '';
                            })()}`}
                          />
                        </div>
                      </div>

                      {/* Overnight Checkbox - only show when start time > end time */}
                      {(() => {
                        try {
                          const start = new Date(`2000-01-01T${localConfig.timeframeStart}`);
                          const end = new Date(`2000-01-01T${localConfig.timeframeEnd}`);
                          const shouldShowOvernight = !isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start;
                          
                          if (shouldShowOvernight) {
                            return (
                              <div className="overnight-checkbox-section bg-white dark:bg-[#0f172a]">
                                <label className="overnight-checkbox-label dark:text-white">
                                  <input
                                    type="checkbox"
                                    checked={localConfig.timeframeOvernight}
                                    onChange={(e) => updateConfig("timeframeOvernight", e.target.checked)}
                                    className="overnight-checkbox mr-2"
                                  />
                                  <span className="flex items-center gap-2">
                                    <span>🌙</span>
                                    <span>This timeframe runs overnight</span>
                                  </span>
                                </label>
                                <p className="overnight-help-text dark:text-gray-400">
                                  Check this if your timeframe spans across midnight (e.g., 11 PM to 4 AM)
                                </p>
                              </div>
                            );
                          }
                          return null;
                        } catch (error) {
                          return null;
                        }
                      })()}

                      {/* Day Selection */}
                      <div className="timeframe-days-section">
                        <label className="time-label dark:text-white mb-2">Active Days</label>
                        <div className="day-selection">
                          {dayOptions.map((day) => {
                            const isSelected = Array.isArray(localConfig.timeframeDays)
                              ? localConfig.timeframeDays.includes(day.value)
                              : false
                            return (
                              <button
                                key={day.value}
                                type="button"
                                className={`day-button dark:text-white dark:border-none dark:focus:outline-none ${isSelected ? "selected dark:bg-[#3B82F6] dark:text-white dark:border-none dark:focus:outline-none" : "dark:bg-[#0f172a]"}`}
                                onClick={() => toggleTimeframeDay(day.value)}
                              >
                                {day.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {/* Visual Summary */}
                      <div className={`timeframe-preview bg-white dark:bg-[#0f172a] border dark:border-[#334155] ${(() => {
                        const validation = validateTimeframe();
                        return !validation.isValid ? 'error-state' : '';
                      })()}`}>
                        <div className="timeframe-preview-header">
                          <span className="preview-icon">{(() => {
                            const validation = validateTimeframe();
                            return !validation.isValid ? '⚠️' : '🕐';
                          })()}</span>
                          <span className="preview-title dark:text-white">{(() => {
                            const validation = validateTimeframe();
                            return !validation.isValid ? 'Invalid Configuration' : 'Active Period';
                          })()}</span>
                        </div>
                        <div className="timeframe-preview-content">
                          {(() => {
                            const validation = validateTimeframe();
                            if (!validation.isValid) {
                              return (
                                <div className="error-message dark:text-red-400 text-red-600">
                                  {validation.error}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <div className="time-range text-blue-400">
                            {(() => {
                              // Convert 24-hour format to 12-hour format with AM/PM
                              const formatTime = (time24) => {
                                if (!time24 || typeof time24 !== 'string') return '';
                                const parts = time24.split(':');
                                if (parts.length < 2) return time24; // Return as-is if invalid format
                                const hours = parseInt(parts[0]);
                                const minutes = parts[1];
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                                return `${hour12}:${minutes} ${ampm}`;
                              };
                              
                              const startTime = formatTime(localConfig.timeframeStart);
                              const endTime = formatTime(localConfig.timeframeEnd);
                              
                              if (!startTime || !endTime) {
                                return 'Invalid time range';
                              }
                              
                              // Check for invalid range based on overnight checkbox
                              try {
                                const start = new Date(`2000-01-01T${localConfig.timeframeStart}`);
                                const end = new Date(`2000-01-01T${localConfig.timeframeEnd}`);
                                
                                // Check if start and end times are identical
                                if (localConfig.timeframeStart === localConfig.timeframeEnd) {
                                  return 'Invalid time range';
                                }
                                
                                // Check if end time is before start time and overnight checkbox is not checked
                                if (end <= start && !localConfig.timeframeOvernight) {
                                  return 'Invalid time range';
                                }
                                
                                // Check if overnight checkbox is checked but times don't actually span overnight
                                if (localConfig.timeframeOvernight && end > start) {
                                  return 'Invalid time range';
                                }
                              } catch (error) {
                                // Continue with display if date parsing fails
                              }
                              
                              return `${startTime} - ${endTime}`;
                            })()}
                          </div>
                          <div className="time-note dark:text-gray-400">
                            {(() => {
                              // Show selected days
                              const selectedDays = Array.isArray(localConfig.timeframeDays) ? localConfig.timeframeDays : [];
                              const allDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                              
                              let dayText = "";
                              if (selectedDays.length === 7) {
                                dayText = "Daily";
                              } else if (selectedDays.length === 5 && 
                                        selectedDays.includes("Monday") && selectedDays.includes("Tuesday") && 
                                        selectedDays.includes("Wednesday") && selectedDays.includes("Thursday") && 
                                        selectedDays.includes("Friday")) {
                                dayText = "Weekdays";
                              } else if (selectedDays.length === 2 && 
                                        selectedDays.includes("Saturday") && selectedDays.includes("Sunday")) {
                                dayText = "Weekends";
                              } else {
                                // Show abbreviated day names
                                const dayMap = {
                                  "Sunday": "Sun", "Monday": "Mon", "Tuesday": "Tue", 
                                  "Wednesday": "Wed", "Thursday": "Thu", "Friday": "Fri", "Saturday": "Sat"
                                };
                                dayText = selectedDays.map(day => dayMap[day]).join(", ");
                              }
                              
                              return dayText;
                            })()} • {(() => {
                              // Check if we have valid time values
                              if (!localConfig.timeframeStart || !localConfig.timeframeEnd) {
                                return '11 hours active';
                              }
                              
                              try {
                                const start = new Date(`2000-01-01T${localConfig.timeframeStart}`);
                                const end = new Date(`2000-01-01T${localConfig.timeframeEnd}`);
                                
                                // Check if dates are valid
                                if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                                  return '11 hours active';
                                }
                                
                                // Handle different timeframe scenarios
                                let diff;
                                
                                // Check if start and end times are identical
                                if (localConfig.timeframeStart === localConfig.timeframeEnd) {
                                  return 'Invalid time range';
                                }
                                
                                // Handle overnight timeframes
                                if (localConfig.timeframeOvernight && end <= start) {
                                  // Overnight timeframe: calculate as (24:00 - start) + (end - 00:00)
                                  diff = (24 * 60 * 60 * 1000 - start.getTime() + end.getTime()) / (1000 * 60 * 60);
                                  // Normalize to 24-hour period
                                  diff = diff % 24;
                                } else if (!localConfig.timeframeOvernight && end > start) {
                                  // Same day timeframe
                                  diff = (end - start) / (1000 * 60 * 60);
                                } else {
                                  // Invalid configuration
                                  return 'Invalid time range';
                                }
                                
                                const exactHours = diff;
                                const roundedHours = Math.round(diff);
                                
                                // Only show "~" if it's not exactly a whole number
                                const prefix = exactHours === roundedHours ? '' : '~';
                                return `${prefix}${roundedHours} hour${roundedHours !== 1 ? 's' : ''} active`;
                              } catch (error) {
                                return '11 hours active';
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label className="dark:text-white">End Schedule</label>
                <div className="select-container no-icon">
                  <select
                    value={localConfig.endType}
                    onChange={(e) => updateConfig("endType", e.target.value)}
                    className="form-select dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                  >
                    <option value="never">Never ends</option>
                    <option value="date">Ends on date</option>
                    <option value="occurrences">Ends after occurrences</option>
                  </select>
                </div>
              </div>

              {localConfig.endType === "date" && (
                <div className="form-group">
                  <label className="dark:text-white">End Date</label>
                  <input
                    type="date"
                    value={localConfig.endDate}
                    onChange={(e) => updateConfig("endDate", e.target.value)}
                    className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    min={localConfig.startDate}
                  />
                </div>
              )}

              {localConfig.endType === "occurrences" && (
                <div className="form-group">
                  <label className="dark:text-white">Number of Occurrences</label>
                  <input
                    type="number"
                    value={localConfig.endOccurrences}
                    onChange={(e) => updateConfig("endOccurrences", Number.parseInt(e.target.value))}
                    className="form-input dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
                    min="1"
                  />
                </div>
              )}
            </>
          )}

          {/* Schedule summary moved here and updated */}
          {localConfig.triggerType === "schedule" && (
            <div className="schedule-summary dark:bg-[#1f2937] dark:text-white dark:border-none">
              <div className="schedule-summary-content">
                {/* Main frequency description */}
                <div className="schedule-summary-title dark:text-white">
                  <strong>Schedule Summary</strong>
                </div>
                
                <div className="schedule-summary-description dark:text-gray-300">
                  {localConfig.frequency === "minute" && `This workflow will run every ${localConfig.minuteInterval} minute${localConfig.minuteInterval !== 1 ? 's' : ''}`}
                  {localConfig.frequency === "hour" && `This workflow will run at ${localConfig.minuteValue} minutes past every hour`}
                  {localConfig.frequency === "day" && `This workflow will run daily at ${localConfig.timeValue}`}
                  {localConfig.frequency === "week" &&
                    `This workflow will run weekly at ${localConfig.timeValue} (${localConfig.timezone}) on ${Array.isArray(localConfig.dayValue) ? localConfig.dayValue.join(", ") : localConfig.dayValue}`}
                  {localConfig.frequency === "month" &&
                    `This workflow will run monthly at ${localConfig.timeValue} (${localConfig.timezone}) on the ${localConfig.monthValue} of each month`}
                  {localConfig.frequency === "quarter" &&
                    `This workflow will run quarterly at ${localConfig.timeValue} (${localConfig.timezone}) on the ${localConfig.monthValue} of each quarter. ${localConfig.quarterValue === "Q1" ? "Starting January: runs in January, April, July, and October" : localConfig.quarterValue === "Q2" ? "Starting February: runs in February, May, August, and November" : "Starting March: runs in March, June, September, and December"}. For example, if you select the 15th, it will run on the 15th of each quarter month.`}
                  {localConfig.frequency === "biannual" &&
                    `This workflow will run biannually at ${localConfig.timeValue} (${localConfig.timezone}) on the ${localConfig.monthValue} of each half-year. ${localConfig.halfYearValue === "H1" ? "Runs in January and July" : localConfig.halfYearValue === "H2" ? "Runs in February and August" : localConfig.halfYearValue === "H3" ? "Runs in March and September" : localConfig.halfYearValue === "H4" ? "Runs in April and October" : localConfig.halfYearValue === "H5" ? "Runs in May and November" : "Runs in June and December"}. For example, if you select the 1st, it will run on the 1st of both months.`}
                  {localConfig.frequency === "annual" &&
                    `This workflow will run annually at ${localConfig.timeValue} (${localConfig.timezone}) on the ${localConfig.monthValue} of ${localConfig.annualMonthValue || "January"}. For example, if you select January 1st, it will run every January 1st.`}
                  
                  {/* Add timeframe restrictions */}
                  {localConfig.enableTimeframe && (localConfig.frequency === "minute" || localConfig.frequency === "hour") && (
                    <span>
                      {", but only during active hours: "}
                      <strong>
                        {(() => {
                          // Convert 24-hour to 12-hour format
                          const formatTime12 = (time24) => {
                            if (!time24) return '';
                            const [hours, minutes] = time24.split(':');
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return `${hour12}:${minutes} ${ampm}`;
                          };
                          
                          return `${formatTime12(localConfig.timeframeStart)} - ${formatTime12(localConfig.timeframeEnd)}`;
                        })()}
                      </strong>
                      {" "}
                      {(() => {
                        const selectedDays = Array.isArray(localConfig.timeframeDays) ? localConfig.timeframeDays : [];
                        if (selectedDays.length === 7) {
                          return "daily";
                        } else if (selectedDays.length === 5 && 
                                  selectedDays.includes("Monday") && selectedDays.includes("Tuesday") && 
                                  selectedDays.includes("Wednesday") && selectedDays.includes("Thursday") && 
                                  selectedDays.includes("Friday")) {
                          return "on weekdays";
                        } else if (selectedDays.length === 2 && 
                                  selectedDays.includes("Saturday") && selectedDays.includes("Sunday")) {
                          return "on weekends";
                        } else {
                          // Sort days in Sun-Sat order
                          const dayOrder = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                          const dayMap = {
                            "Sunday": "Sun", "Monday": "Mon", "Tuesday": "Tue", 
                            "Wednesday": "Wed", "Thursday": "Thu", "Friday": "Fri", "Saturday": "Sat"
                          };
                          const sortedDays = dayOrder.filter(day => selectedDays.includes(day));
                          return `on ${sortedDays.map(day => dayMap[day]).join(", ")}`;
                        }
                      })()}
                    </span>
                  )}
                  .
                </div>

                {/* Add detailed timing information */}
                <div className="schedule-summary-dates">
                  {/* Activation date/time */}
                  <div className="schedule-summary-date dark:text-white">
                    <strong className="dark:text-white">
                      {(() => {
                        // Determine if the activation date is in the past or future
                        const activationDate = localConfig.startDate && localConfig.startTime
                          ? new Date(`${localConfig.startDate}T${localConfig.startTime}`)
                          : localConfig.startDate
                            ? new Date(localConfig.startDate)
                            : new Date();
                        
                        const now = new Date();
                        return activationDate < now ? "Trigger became active:" : "Trigger becomes active:";
                      })()}
                    </strong>{" "}
                    {localConfig.startDate
                      ? localConfig.startTime
                        ? new Date(`${localConfig.startDate}T${localConfig.startTime}`).toLocaleString(undefined, {
                            weekday: "long",
                            year: "numeric",
                            month: "long", 
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : new Date(localConfig.startDate).toLocaleDateString()
                      : new Date().toLocaleString(undefined, {
                          weekday: "long",
                          year: "numeric", 
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                  </div>

                  {/* Execution schedule details */}
                  {localConfig.frequency === "quarter" && (
                    <div className="schedule-summary-date dark:text-gray-400">
                      <em>
                        {localConfig.quarterValue === "Q1" 
                          ? "Will run on the 1st, 4th, 7th, and 10th months of each year"
                          : localConfig.quarterValue === "Q2"
                          ? "Will run on the 2nd, 5th, 8th, and 11th months of each year"
                          : "Will run on the 3rd, 6th, 9th, and 12th months of each year"
                        } at {localConfig.monthValue} of each month
                      </em>
                    </div>
                  )}

                  {localConfig.frequency === "biannual" && (
                    <div className="schedule-summary-date dark:text-gray-400">
                      <em>
                        {localConfig.halfYearValue === "H1" 
                          ? "Will run on the 1st and 7th months of each year"
                          : localConfig.halfYearValue === "H2"
                          ? "Will run on the 2nd and 8th months of each year"
                          : localConfig.halfYearValue === "H3"
                          ? "Will run on the 3rd and 9th months of each year"
                          : localConfig.halfYearValue === "H4"
                          ? "Will run on the 4th and 10th months of each year"
                          : localConfig.halfYearValue === "H5"
                          ? "Will run on the 5th and 11th months of each year"
                          : "Will run on the 6th and 12th months of each year"
                        } at {localConfig.monthValue} of each month
                      </em>
                    </div>
                  )}

                  {localConfig.frequency === "annual" && (
                    <div className="schedule-summary-date dark:text-gray-400">
                      <em>
                        Will run once per year on {localConfig.monthValue} of {localConfig.annualMonthValue || "January"}
                      </em>
                    </div>
                  )}

                  {/* First run explanation */}
                  <div className="schedule-summary-date dark:text-gray-400">
                    <em>
                      {(() => {
                        // Determine if the activation date is in the past or future
                        const activationDate = localConfig.startDate && localConfig.startTime
                          ? new Date(`${localConfig.startDate}T${localConfig.startTime}`)
                          : localConfig.startDate
                            ? new Date(localConfig.startDate)
                            : new Date();
                        
                        const now = new Date();
                        return activationDate < now 
                          ? "First run occurred/will occur at the next scheduled time after the trigger became active"
                          : "First run will occur at the next scheduled time after the trigger becomes active";
                      })()}
                    </em>
                  </div>

                  {/* Add end information based on endType */}
                  <div className="schedule-summary-date dark:text-white">
                    <strong className="dark:text-white">
                      {localConfig.endType === "never" ? "Duration:" : "Ends:"}
                    </strong>{" "}
                    {localConfig.endType === "never"
                      ? "Runs indefinitely until manually stopped"
                      : localConfig.endType === "date"
                        ? localConfig.endDate
                          ? `Stops running after ${new Date(localConfig.endDate).toLocaleDateString(undefined, {
                              weekday: "long",
                              year: "numeric", 
                              month: "long",
                              day: "numeric"
                            })}`
                          : "End date not set"
                        : localConfig.endType === "occurrences"
                          ? localConfig.endOccurrences > 0
                            ? `Stops after ${localConfig.endOccurrences} execution${localConfig.endOccurrences > 1 ? "s" : ""}`
                            : "Number of occurrences not set"
                          : "Runs indefinitely until manually stopped"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            {(() => {
              const validation = validateTimeframe();
              return (
                <div className="form-actions-container">
                  {!validation.isValid && (
                    <div className="validation-error dark:text-red-400 text-red-600 text-sm mb-2">
                      {validation.error}
                    </div>
                  )}
                  <button 
                    className={`save-button w-full ${!validation.isValid ? 'disabled' : ''}`} 
                    onClick={handleSave}
                    disabled={!validation.isValid}
                  >
                    {isNew ? "Create" : "Save changes"}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="triggers-panel bg-white dark:bg-card dark:border-border">
      {/* Triggers List */}
      <div className={`triggers-list ${activeTrigger || isCreatingTrigger ? "with-config border-[#e0e0e0] dark:border-[#1f2937]" : ""}`}>
        <div className="triggers-header dark:border-b-2 dark:border-[#1f2937]">
          <div className="triggers-title">
            <span>Triggers</span>
            <button className="info-button">
              <span>ⓘ</span>
            </button>
          </div>
          <div className="triggers-actions">
            <button
              className="add-button dark:text-white dark:hover:bg-[#0070f3]"
              onClick={() => {
                // Close any active trigger panel before opening the new trigger panel
                setActiveTrigger(null)
                setIsCreatingTrigger(true)
                setLocalConfig({
                  triggerType: "schedule",
                  scheduleType: "interval",
                  timezone: "UTC",
                  frequency: "day",
                  minuteValue: "0",
                  hourValue: "0",
                  dayValue: ["Monday"],
                  monthValue: "1st",
                  quarterValue: "Q1",
                  halfYearValue: "H1",
                  annualMonthValue: "January",
                  timeValue: "12:00 AM",
                  url: "https://example.com/webhook",
                  name: "",
                  startDate: new Date().toISOString().split("T")[0],
                  startTime: new Date().toLocaleTimeString("en-US", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                  timeframeDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                  timeframeOvernight: false,
                  endType: "never",
                  endDate: "",
                  endOccurrences: 1,
                  minuteInterval: 1,
                })
              }}
            >
              <Plus size={16} />
            </button>
            <button className="close-button dark:text-white dark:hover:bg-red-500" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="triggers-content">
          {/* Schedule section */}
          <div className="trigger-category">
            <h3 className="dark:text-white">Schedule</h3>
            {triggers
              .filter((trigger) => trigger.type === "schedule")
              .map((trigger) => (
                <div key={trigger.id} className={`trigger-item dark:hover:bg-[#1f2937] ${activeTrigger?.id === trigger.id ? "active dark:hover:bg-[#1f2937] dark:bg-[#1f2937]" : ""}`}>
                  <div className="trigger-item-icon">
                    <Calendar size={16} />
                  </div>
                  <div
                    className="trigger-item-content"
                    onClick={() => {
                      setIsCreatingTrigger(false)
                      setActiveTrigger(trigger)
                      setShowDeleteMenu(null)
                      // Ensure minuteInterval is set when editing a trigger
                      const configWithDefaults = {
                        ...trigger.config,
                        minuteInterval: trigger.config.minuteInterval || 1
                      }
                      setLocalConfig(configWithDefaults)
                    }}
                  >
                    <span className="trigger-item-description">
                      {trigger.name && trigger.name !== "Schedule" && trigger.name !== "Webhook"
                        ? trigger.name
                        : trigger.description}
                    </span>
                  </div>
                  <div className="trigger-item-actions">
                    <button
                      className="more-button dark:hover:bg-[#1f2937]"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDeleteMenu(showDeleteMenu === trigger.id ? null : trigger.id)
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>
                    {showDeleteMenu === trigger.id && (
                      <div className="delete-menu dark:bg-red-500 dark:border-0">
                        <button
                          className="delete-button dark:text-white dark:hover:bg-red-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteTrigger(trigger.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={trigger.enabled}
                        onChange={(e) => {
                          e.stopPropagation()
                          toggleTrigger(trigger.id)
                        }}
                      />
                      <span className={`slider round ${trigger.enabled ? "" : "dark:bg-[#1f2937]"}`}></span>
                    </label>
                  </div>
                </div>
              ))}
          </div>

          {/* Webhook/Integration section */}
          <div className="trigger-category">
            <h3 className="dark:text-white">Integrations</h3>
            {triggers
              .filter((trigger) => trigger.type === "acculynx" || trigger.type === "leadperfection" || trigger.type === "aspire" || trigger.type === "webhook" || trigger.type === "integration")
              .map((trigger) => (
                <div key={trigger.id} className={`trigger-item dark:hover:bg-[#1f2937] ${activeTrigger?.id === trigger.id ? "active dark:bg-[#1f2937] dark:hover:bg-[#1f2937]" : ""}`}>
                  <div className="trigger-item-icon">
                    <Link size={16} />
                  </div>
                  <div
                    className="trigger-item-content"
                    onClick={() => {
                      setIsCreatingTrigger(false)
                      setActiveTrigger(trigger)
                      setShowDeleteMenu(null)
                      // Ensure minuteInterval is set when editing a trigger
                      const configWithDefaults = {
                        ...trigger.config,
                        minuteInterval: trigger.config.minuteInterval || 1
                      }
                      setLocalConfig(configWithDefaults)
                    }}
                  >
                    <span className="trigger-item-description">
                      {trigger.name && trigger.name !== "Schedule" && trigger.name !== "Webhook"
                        ? trigger.name
                        : trigger.description}
                    </span>
                  </div>
                  <div className="trigger-item-actions">
                    <button
                      className="more-button dark:hover:bg-[#1f2937]"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDeleteMenu(showDeleteMenu === trigger.id ? null : trigger.id)
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>
                    {showDeleteMenu === trigger.id && (
                      <div className="delete-menu dark:bg-red-500 dark:border-0">
                        <button
                          className="delete-button dark:text-white dark:hover:bg-red-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteTrigger(trigger.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={trigger.enabled}
                        onChange={(e) => {
                          e.stopPropagation()
                          toggleTrigger(trigger.id)
                        }}
                      />
                      <span className={`slider round ${trigger.enabled ? "" : "dark:bg-[#1f2937]"}`}></span>
                    </label>
                  </div>
                </div>
              ))}
          </div>
          {/* Integrations section */}
          {/* <div className="trigger-category">
            <h3>Integrations</h3>
            {triggers
              .filter((trigger) => trigger.type === "integration")
              .map((trigger) => (
                <div key={trigger.id} className={`trigger-item ${activeTrigger?.id === trigger.id ? "active" : ""}`}>
                  <div className="trigger-item-icon">
                    <Link size={16} />
                  </div>
                  <div
                    className="trigger-item-content"
                    onClick={() => {
                      setIsCreatingTrigger(false)
                      setActiveTrigger(trigger)
                      setShowDeleteMenu(null)
                      setLocalConfig(trigger.config)
                    }}
                  >
                    <span className="trigger-item-description">{trigger.name || trigger.description}</span>
                  </div>
                  <div className="trigger-item-actions">
                    <button
                      className="more-button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDeleteMenu(showDeleteMenu === trigger.id ? null : trigger.id)
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>
                    {showDeleteMenu === trigger.id && (
                      <div className="delete-menu">
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteTrigger(trigger.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={trigger.enabled}
                        onChange={(e) => {
                          e.stopPropagation()
                          toggleTrigger(trigger.id)
                        }}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
              ))}
          </div> */}
        </div>
      </div>

      {/* Only render one configuration panel at a time */}
      {activeTrigger && !isCreatingTrigger && renderTriggerConfig()}
      {isCreatingTrigger && !activeTrigger && renderTriggerConfig(true)}
    </div>
  )
}

export default TriggersPanel
